/**
 * Helpers for stabilising the feature-flagged cards in the Home grid (CM-3140).
 *
 * The grid reflows when a flag-gated card appears/disappears after the feature
 * flag fetch resolves. To keep the first paint matching the final layout we
 * cache the last-known flag values per home, so a returning load renders the
 * correct grid immediately and only a true cold load falls back to a skeleton.
 */

export type CachedHomeFlags = {
  incidentReports: boolean
  goals: boolean
  emarV2: boolean
}

const cacheKey = (homeId: string) => `home-grid-flags:${homeId}`

export const readCachedHomeFlags = (
  homeId?: string
): CachedHomeFlags | null => {
  if (!homeId || typeof localStorage === 'undefined') {
    return null
  }
  try {
    const raw = localStorage.getItem(cacheKey(homeId))
    return raw ? (JSON.parse(raw) as CachedHomeFlags) : null
  } catch {
    return null
  }
}

export const writeCachedHomeFlags = (
  homeId: string | undefined,
  flags: CachedHomeFlags
): void => {
  if (!homeId || typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.setItem(cacheKey(homeId), JSON.stringify(flags))
  } catch {
    // localStorage can be full or disabled — caching is best-effort only.
  }
}

export type CardSlotState = 'show' | 'skeleton' | 'hide'

/**
 * State of the feature flag fetch. `error` is distinct from `success` because
 * `useFeatureFlags` exposes all-disabled defaults on failure — treating that as
 * authoritative would both hide cards that should show and (if persisted)
 * corrupt the cache, so a failed fetch falls back to the cached value instead.
 */
export type FlagFetchStatus = 'loading' | 'success' | 'error'

type ResolveCardSlotArgs = {
  /** Non-flag conditions (home state) that must hold for the card to ever show. */
  preconditionMet: boolean
  /** Live flag value from the fetch (only authoritative on success). */
  liveFlagEnabled: boolean
  /** State of the feature flag fetch. */
  status: FlagFetchStatus
  /** Last-known cached flag value for this home, if any. */
  cachedFlagEnabled: boolean | undefined
}

/**
 * Decides whether a flag-gated card should render, show a skeleton placeholder,
 * or be omitted — chosen so the first paint matches the final layout whenever
 * we have either a successful fetch or a cached value.
 */
export const resolveCardSlot = ({
  preconditionMet,
  liveFlagEnabled,
  status,
  cachedFlagEnabled
}: ResolveCardSlotArgs): CardSlotState => {
  if (!preconditionMet) {
    return 'hide'
  }
  // A successful fetch is authoritative.
  if (status === 'success') {
    return liveFlagEnabled ? 'show' : 'hide'
  }
  // Still loading or the fetch failed: prefer the last-known value so the grid
  // keeps the layout it had before.
  if (cachedFlagEnabled !== undefined) {
    return cachedFlagEnabled ? 'show' : 'hide'
  }
  // No cache to fall back on: hold the slot while loading, but on a failed fetch
  // (no retry) settle on the disabled default rather than a stuck skeleton.
  if (status === 'loading') {
    return 'skeleton'
  }
  return liveFlagEnabled ? 'show' : 'hide'
}
